<template>
  <v-card class="qualification-components mt-2">
    <can I="list" a="QualificationComponent">
      <v-toolbar card prominent class="transparent">
        <v-toolbar-title class="title">Qualification Components</v-toolbar-title>
        <v-spacer />

        <v-text-field
          v-model="search"
          placeholder="Search"
          prepend-icon="mdi-magnify"
          type="search"
        />
        <can I="create" a="QualificationComponent">
          <v-tooltip left>
            <v-btn fab small slot="activator" @click="create">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            <span>Add a component</span>
          </v-tooltip>
        </can>
      </v-toolbar>

      <v-dialog v-model="dialog" max-width="800">
        <can I="edit" a="QualificationComponent">
          <v-card>
            <v-card-title>
              <div v-if="editing.id">
                <div class="title">{{ editing.description }}</div>
                <div v-if="editing.reference.length" class="subtitle">
                  {{ editing.reference }}
                </div>
              </div>
              <div class="title" v-else>
                New Qualification Component
              </div>
            </v-card-title>
            <v-card-text>
              <v-form v-model="valid">
                <v-text-field
                  label="Description"
                  v-model="editing.description"
                  required :rules="rules.description"
                  :error-messages="editing.errors && editing.errors.description || []"
                />

                <v-text-field
                  label="Reference"
                  v-model="editing.reference"
                />

                <v-autocomplete multiple chips deletable-chips
                  label="Disciplines"
                  v-model="editing.disciplines"
                  :items="disciplines" item-text="name" item-value="id"
                />

                <v-select
                  label="Grade Scale"
                  v-model="editing.gradeScale"
                  :items="gradeScales"
                />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-dialog max-width="600" v-model="deleteDialog" v-if="editing.id">
                <can I="delete" a="QualificationComponent">
                  <v-btn flat slot="activator" color="error">
                    <v-icon left>mdi-delete</v-icon> Delete
                  </v-btn>

                  <v-card>
                    <v-card-title>
                      <div>
                        <div class="headline">
                          Are you sure?
                        </div>
                        <div class="subheading">
                          Really remove {{ editing.description }}?
                          <span v-if="editing.reference">({{ editing.reference }})</span>
                        </div>
                      </div>
                    </v-card-title>
                    <v-card-text>
                      <p>This will remove this combination from appearing in subject and course suggestions.</p>
                      <p>
                        It will also unlink all linked profile qualification components,
                        causing them to lose the disciplines associated with this component.
                      </p>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn flat block color="grey" @click="deleteDialog = false">cancel</v-btn>
                      <v-btn block color="error" @click.native="remove" >
                        Delete
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </can>
              </v-dialog>

              <v-spacer />

              <v-btn flat @click="dialog = false" color="grey">
                Cancel
              </v-btn>
              <v-btn @click="submit" :disabled="!valid" color="primary">
                {{ this.editing.id ? 'Update' : 'Create' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </can>
      </v-dialog>

      <v-data-table
        :headers="headers" :pagination.sync="pagination"
        :items="qualificationComponents"
        :loading="loading"
        :search="search"
      >
        <template slot="items" slot-scope="{ item }">
          <td>{{ item.id }}</td>
          <td>{{ item.reference }}</td>
          <td>{{ item.description }}</td>
          <td>{{ item.gradeScale }}</td>
          <td>
            <v-chip label v-for="discipline in disciplines.filter(d => item.disciplines.indexOf(d.id) >= 0)" :key="`discipline-${discipline.id}`">
              {{ discipline.name }}
            </v-chip>
          </td>

          <td class="text-xs-right">
            <can I="edit" a="QualificationComponent">
              <v-btn small flat fab @click="edit(item)" color="secondary">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </can>
          </td>
        </template>
      </v-data-table>
    </can>
  </v-card>
</template>

<script>
const newQualificationComponent = {
  id: null,
  reference: '',
  description: '',
  disciplines: [],
  gradeScale: 'Percentage'
}

// Helpers
import { mapGetters } from 'vuex'

// Data
import gradeScales from '@/data/grade_scales'

// Vue Component
export default {
  props: {
    institution: Object,
    qualificationType: Object
  },
  created() {
    this.$store.dispatch('disciplines/fetchAll')
  },
  data() {
    return {
      // Search
      search: '',
      // Toggle
      dialog: false,
      deleteDialog: false,
      editing: {},
      // Validation
      valid: false,
      rules: {
        description: [v => !!v || 'You must provide a description']
      },
      // Data
      gradeScales,
      // Table
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Reference', value: 'reference' },
        { text: 'Description', value: 'description' },
        { text: 'Grade Scale', value: 'gradeScale', sortable: false },
        { text: 'Disciplines', value: 'disciplines.length' },
        { text: 'Actions', sortable: false, align: 'right' }
      ],
      pagination: { rowsPerPage: 25 }
    }
  },
  computed: {
    ...mapGetters({
      allQualificationComponents: 'admin/qualificationComponents/all',
      loading: 'admin/qualificationComponents/loading',
      disciplines: 'disciplines/all'
    }),
    qualificationComponents() {
      if (this.institution) {
        return this.allQualificationComponents.filter(qc => qc.institution && qc.institution.id == this.institution.id)
      } else if (this.qualificationType) {
        return this.allQualificationComponents.filter(
          qc => qc.qualificationType && qc.qualificationType.id == this.qualificationType.id
        )
      } else {
        return []
      }
    }
  },
  methods: {
    create() {
      if (this.institution) {
        this.editing = Object.assign({ errors: {}, institution: this.institution }, newQualificationComponent)
      }
      if (this.qualificationType) {
        this.editing = Object.assign(
          { errors: {}, qualificationType: this.qualificationType },
          newQualificationComponent
        )
      }
      this.dialog = true
    },
    edit(component) {
      this.editing = Object.assign({ errors: {} }, component)
      this.dialog = true
    },
    submit() {
      if (this.editing.id) {
        // Update
        this.$store
          .dispatch('admin/qualificationComponents/update', this.editing)
          .then(invalid => (this.dialog = !!invalid))
      } else {
        // Create
        this.$store
          .dispatch('admin/qualificationComponents/create', this.editing)
          .then(invalid => (this.dialog = !!invalid))
      }
    },
    remove() {
      this.$store.dispatch('admin/qualificationComponents/destroy', this.editing.id).then(() => {
        if (this.institution) {
          this.editing = Object.assign({ errors: {}, institution: this.institution }, newQualificationComponent)
        }
        if (this.qualificationType) {
          this.editing = Object.assign(
            { errors: {}, qualificationType: this.qualificationType },
            newQualificationComponent
          )
        }
        this.dialog = false
        this.deleteDialog = false
      })
    }
  }
}
</script>
